import { cn } from '@/lib/cn';

/**
 * A switch, not a checkbox: it commits immediately and has no submit step.
 *
 * The state is never carried by the fill alone — every caller renders the word
 * for the current state next to it — because this control is read at arm's
 * length, outdoors, in a hurry, and because a colour-blind trader turning their
 * traffic off and not being sure it took is a trader who stops trusting it.
 *
 * The visible track is 48x28, but the button around it is 44px tall so the
 * thumb target meets WCAG 2.5.5 without the control growing to fill a row.
 */
export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The accessible name. Required — a bare switch says nothing to a reader. */
  label: string;
  disabled?: boolean;
  /** Ids of the text explaining the current state, announced with the switch. */
  describedBy?: string;
  className?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  describedBy,
  className,
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'focus-ring inline-flex h-11 shrink-0 items-center rounded-md px-0.5',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'relative flex h-7 w-12 items-center rounded-full border',
          'transition-colors duration-fast ease-out',
          checked ? 'border-accent-600 bg-accent-600' : 'border-line-strong bg-sunken',
        )}
      >
        <span
          className={cn(
            // The knob is the state indicator, so its edge has to survive the
            // off state, where a white knob sits on a pale track. fg-muted
            // clears 4:1 against both.
            'absolute h-5 w-5 rounded-full border border-fg-muted bg-n-0',
            'transition-[left] duration-fast ease-out',
            checked ? 'left-[1.5rem]' : 'left-0.5',
          )}
        />
      </span>
    </button>
  );
}
